// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useEffect, useMemo, useState } from 'react';

import { type Project, type ProjectStatusFilter, useEvolveDelete, useEvolvePaginatedGet } from '@evolvemep/api-ts';
import { Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { RowSelectionOptions } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';

import { TwoPanelLayout } from 'app/Layout/TwoPanelLayout';
import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { ActiveAllSelect } from 'components/Mantine/GlobalFilters/ActiveAllSelect';
import { VariableTrackingModal } from 'components/Mantine/Modals/VariableTrackingModal';
import { NoContentFallback } from 'components/NoContentFallback/NoContentFallback';
import { AgGridContainer } from 'helpers/ag-grid/AgGridContainer';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { TABLE_VIEW_TYPE } from 'helpers/ag-grid/TableViewPanel/constants';
import { useServerSideGrid } from 'helpers/ag-grid/useServerSideGrid';
import { isNotNil } from 'helpers/isNotNil';
import { useActiveAllFilterState } from 'hooks/useActiveAllFilterState';
import { EditableWrapper } from 'modules/Admin/Facilities/EditableWrapper';
import { getProjectsColDef } from 'modules/Admin/Projects/columnDefs';
import { CreateProjectModal } from 'modules/Admin/Projects/CreateProjectModal';
import { OpenCloseProjectModal } from 'modules/Admin/Projects/OpenCloseProjectModal';
import { ProjectDetailsPane } from 'modules/Admin/Projects/ProjectProperties/ProjectDetailsPane';

const rowSelection: RowSelectionOptions = {
  mode: 'singleRow',
  enableClickSelection: false,
  checkboxes: false,
};
const ACTIVE_PROJECT_STATUSES: ProjectStatusFilter[] = ['Active'];
const ALL_PROJECT_STATUSES: ProjectStatusFilter[] = ['All'];

export const ProjectsPage = () => {
  const [projectToStatusChange, setProjectToStatusChange] = useState<Project>();
  const [projectToDelete, setProjectToDelete] = useState<Project>();
  const { apiCall: deleteCall, loading: deleting } = useEvolveDelete('shop/project/:projectId');
  const [projectStatus, selectProps] = useActiveAllFilterState(ACTIVE_PROJECT_STATUSES, ALL_PROJECT_STATUSES);
  const { fetchPage, abortAll, setDefaultOpts } = useEvolvePaginatedGet('shop/project');

  useEffect(() => {
    setDefaultOpts({
      defaultConfig: {
        params: {
          // Drive filtering outside the grid: 'Active' or 'All'
          status: projectStatus[0],
        },
      },
    });
  }, [projectStatus, setDefaultOpts]);

  const colDef = useMemo(
    () =>
      getProjectsColDef({
        deleteProject: setProjectToDelete,
        statusChangeProject: setProjectToStatusChange,
      }),
    [],
  );

  const { agGridProps, gridApi } = useServerSideGrid({
    tableName: 'projects',
    tableViewType: TABLE_VIEW_TYPE.PROJECTS,
    exportFileName: 'Projects',
    fetchPage,
    abortAll,
    colDef,
    rowId: 'projectId',
  });

  return (
    <EditableWrapper localStorageKey="LAST_VIEWED_PROJECT">
      {({ selectItem: selectProject, editing, setEditing, selectedItem: selectedProject }) => (
        <TwoPanelLayout
          mainPanel={
            <>
              <BasePageHeader
                title="Projects"
                topRightComponent={
                  <>
                    <ActiveAllSelect {...selectProps} dataLabel="Projects" withLabel={false} />
                    <CreateProjectModal
                      editing={editing}
                      onProjectCreated={(p) => {
                        selectProject(p);
                        refreshGrid(gridApi);
                      }}
                    />
                  </>
                }
              />
              <AgGridContainer>
                {(applyWrap) => (
                  <AgGridReact<Project>
                    {...agGridProps}
                    onFirstDataRendered={applyWrap}
                    rowSelection={rowSelection}
                    onSelectionChanged={(e) => {
                      const selected = e.api.getSelectedNodes()[0];
                      if (isNotNil(selected) && isNotNil(selected.data)) {
                        selectProject(selected.data);
                      }
                    }}
                    onModelUpdated={(e) => {
                      if (isNotNil(selectedProject)) {
                        e.api.getRowNode(selectedProject.projectId)?.setSelected(true, true);
                      }
                    }}
                  />
                )}
              </AgGridContainer>

              <VariableTrackingModal
                title="Delete project"
                variable={projectToDelete}
                onConfirm={async (project) => {
                  await deleteCall({ url: `shop/project/${project.projectId}` }).then(() => {
                    notifications.show({
                      title: 'Successfully deleted',
                      message: `Deleted project ${project.projectName}.`,
                      color: 'green',
                    });
                    setProjectToDelete(undefined);
                    selectProject(null);
                    refreshGrid(gridApi);
                  });
                }}
                onClose={() => setProjectToDelete(undefined)}
                loading={deleting}
                confirmationText="Delete"
                alertState={{
                  title: 'Cannot delete project',
                  showAlert: !!projectToDelete?.hasWorkOrders,
                  message: (
                    <Text>
                      <b>{projectToDelete?.projectName}</b> contains work orders and can not be deleted.
                    </Text>
                  ),
                }}
              >
                {(project) => (
                  <Text>
                    Are you sure you want to delete <b>{project.projectName}</b>?
                  </Text>
                )}
              </VariableTrackingModal>

              {projectToStatusChange && (
                <OpenCloseProjectModal
                  project={projectToStatusChange}
                  onProjectUpdated={(p) => {
                    if (p.projectId === selectedProject?.projectId) {
                      if (p.status === 'Closed' && selectProps.value === 'active') {
                        selectProject(null);
                      } else {
                        selectProject(p, true);
                      }
                    }
                    refreshGrid(gridApi);
                  }}
                  opened
                  onClose={() => {
                    setProjectToStatusChange(undefined);
                  }}
                />
              )}
            </>
          }
          detailPanel={
            isNotNil(selectedProject) ? (
              <ProjectDetailsPane
                selectedProject={selectedProject}
                statusChangeProject={() => {
                  setProjectToStatusChange(selectedProject);
                }}
                onProjectUpdated={(f) => {
                  selectProject(f, true);
                  refreshGrid(gridApi);
                }}
                editing={editing}
                setEditing={setEditing}
              />
            ) : (
              <NoContentFallback message="Select a project." />
            )
          }
        />
      )}
    </EditableWrapper>
  );
};
